
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Award, Search, Mail, User, MapPin, Music, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { award_organization } from '@/types/awardMembership';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import LoadingState from './match/LoadingState';

// Schema for form validation
const formSchema = z.object({
  organization: z.string().optional(),
  category: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Simplified profile data structure to match what's returned by Supabase
interface ProfileData {
  id: string;
  display_name?: string;
  avatar_url?: string;
  role?: string;
  location?: string; 
  genres?: string[];
  [key: string]: any;
}

// Member with profile structure to match the query results
interface MemberWithProfile {
  id: string;
  user_id: string;
  organization: award_organization;
  years_of_membership?: number;
  voting_categories: string[];
  allow_campaign_contact: boolean;
  created_at: string;
  updated_at: string;
  profile: ProfileData;
}

// Mock data for award members
const mockMembers: MemberWithProfile[] = [
  {
    id: '1',
    user_id: 'user1',
    organization: 'Grammy',
    years_of_membership: 5,
    voting_categories: ['Best New Artist', 'Album of the Year', 'Song of the Year'],
    allow_campaign_contact: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      id: 'prof1',
      display_name: 'Sarah Johnson',
      avatar_url: 'https://i.pravatar.cc/150?img=32',
      role: 'Producer & Songwriter',
      location: 'Los Angeles, CA',
      genres: ['Pop', 'R&B', 'Electronic']
    }
  },
  {
    id: '2',
    user_id: 'user2',
    organization: 'Billboard',
    years_of_membership: 3,
    voting_categories: ['Top New Artist', 'Top Billboard 200 Album'],
    allow_campaign_contact: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      id: 'prof2',
      display_name: 'Marcus Chen',
      avatar_url: 'https://i.pravatar.cc/150?img=11',
      role: 'A&R Executive',
      location: 'New York, NY',
      genres: ['Hip-Hop', 'Rap', 'Alternative']
    }
  },
  {
    id: '3',
    user_id: 'user3',
    organization: 'BET',
    years_of_membership: 7,
    voting_categories: ['Video of the Year', 'Best New Artist', 'Album of the Year'],
    allow_campaign_contact: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      id: 'prof3',
      display_name: 'Tiana Rodriguez',
      avatar_url: 'https://i.pravatar.cc/150?img=5',
      role: 'Music Journalist',
      location: 'Atlanta, GA',
      genres: ['R&B', 'Soul', 'Hip-Hop']
    }
  },
  {
    id: '4',
    user_id: 'user4',
    organization: 'ASCAP',
    years_of_membership: 10,
    voting_categories: ['Songwriter of the Year', 'Publisher of the Year'],
    allow_campaign_contact: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      id: 'prof4',
      display_name: 'David Williams',
      avatar_url: 'https://i.pravatar.cc/150?img=54',
      role: 'Songwriter & Publisher',
      location: 'Nashville, TN',
      genres: ['Country', 'Pop', 'Folk']
    }
  },
  {
    id: '5',
    user_id: 'user5',
    organization: 'MTV',
    years_of_membership: 4,
    voting_categories: ['Video of the Year', 'Best New Artist', 'Artist of the Year'],
    allow_campaign_contact: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    profile: {
      id: 'prof5',
      display_name: 'Jasmine Taylor',
      avatar_url: 'https://i.pravatar.cc/150?img=17',
      role: 'Video Director',
      location: 'Miami, FL',
      genres: ['Pop', 'Electronic', 'Dance']
    }
  }
];

const AwardMemberSearch: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [members, setMembers] = useState<MemberWithProfile[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [useDummyData, setUseDummyData] = useState(true); // Flag to use dummy data

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organization: '',
      category: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    setHasSearched(true);
    
    try {
      if (useDummyData) {
        // Filter dummy data based on search criteria (optional - for demo purposes)
        let filteredMembers = [...mockMembers];
        
        if (values.organization && values.organization !== 'any') {
          filteredMembers = filteredMembers.filter(
            member => member.organization === values.organization
          );
        }
        
        if (values.category && values.category.trim() !== '') {
          const searchTerm = values.category.toLowerCase().trim();
          filteredMembers = filteredMembers.filter(member => 
            member.voting_categories.some(category => 
              category.toLowerCase().includes(searchTerm)
            )
          );
        }
        
        // Simulate network delay
        setTimeout(() => {
          setMembers(filteredMembers);
          setIsLoading(false);
        }, 1000);
      } else {
        // Original Supabase query logic
        let query = supabase
          .from('award_memberships')
          .select(`
            *,
            profile:profiles(*)
          `)
          .eq('allow_campaign_contact', true);
        
        if (values.organization && values.organization !== 'any') {
          // Type assertion to ensure the string is treated as a valid award_organization type
          query = query.eq('organization', values.organization as award_organization);
        }
        
        if (values.category && values.category.trim() !== '') {
          query = query.contains('voting_categories', [values.category.trim()]);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Type assertion to handle the profile data conversion
        const memberData = data as unknown as MemberWithProfile[];
        setMembers(memberData.length > 0 ? memberData : mockMembers);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error searching award members:', error);
      toast.error('Failed to search award members');
      // Fallback to mock data on error
      setMembers(mockMembers);
      setIsLoading(false);
    }
  };

  const renderMemberCard = (member: MemberWithProfile) => {
    const profile = member.profile;
    
    if (!profile) {
      return null;
    }
    
    const initials = profile.display_name 
      ? profile.display_name.substring(0, 2).toUpperCase()
      : 'US';
    
    return (
      <Card key={member.id} className="mb-4">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={profile.avatar_url || undefined} alt={profile.display_name || 'User'} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{profile.display_name || profile.id}</h3>
                  {profile.role && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{profile.role}</span>
                    </p>
                  )}
                </div>
                <Badge className="flex items-center gap-1 bg-primary">
                  <Award className="h-3 w-3" />
                  <span>{member.organization}</span>
                </Badge>
              </div>
              
              {profile.location && (
                <p className="text-sm flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span>{profile.location}</span>
                </p>
              )}
              
              {member.years_of_membership && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Voting member for:</span>{' '}
                  <span className="font-medium">{member.years_of_membership} years</span>
                </p>
              )}
              
              {member.voting_categories?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Votes in categories:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.voting_categories.map(category => (
                      <Badge key={category} variant="outline" className="text-xs">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {profile.genres?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Genres:</p>
                  <div className="flex flex-wrap gap-1">
                    {profile.genres.map(genre => (
                      <Badge key={genre} variant="secondary" className="text-xs flex items-center gap-1">
                        <Music className="h-2 w-2" />
                        <span>{genre}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="pt-2">
                <Button variant="outline" size="sm" className="gap-2">
                  <Mail className="h-4 w-4" />
                  <span>Contact</span>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Find Award Voting Members</CardTitle>
          <CardDescription>
            Connect with voting members of major music awards for your campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="organization"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Award Organization</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue="any"
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Any organization" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="any">Any organization</SelectItem>
                          <SelectItem value="Grammy">Grammy Awards</SelectItem>
                          <SelectItem value="Billboard">Billboard Music Awards</SelectItem>
                          <SelectItem value="BET">BET Awards</SelectItem>
                          <SelectItem value="MTV">MTV Awards</SelectItem>
                          <SelectItem value="ASCAP">ASCAP Awards</SelectItem>
                          <SelectItem value="BMI">BMI Awards</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Search by voting category"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="gap-2"
                >
                  <Search className="h-4 w-4" />
                  {isLoading ? 'Searching...' : 'Search Members'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {hasSearched && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Search Results</h2>
          
          {isLoading ? (
            <LoadingState />
          ) : members.length === 0 ? (
            <Card className="bg-muted/50">
              <CardContent className="py-8 text-center">
                <Award className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">No members found</p>
                <p className="text-muted-foreground">
                  Try adjusting your search criteria to find more results
                </p>
              </CardContent>
            </Card>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground mb-4">Found {members.length} members matching your criteria</p>
              {members.map(renderMemberCard)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AwardMemberSearch;
